<template>
  <v-dialog :value="value" max-width="480px" @input="$emit('input', $event)">
    <v-card class="pa-2">
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-subtitle-1 font-weight-bold">
          {{ isEdit ? 'Редактирование интервала' : 'Новый интервал доступности' }}
        </span>
        <v-btn icon small @click="$emit('input', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="text-caption grey--text text--darken-1 mb-1">Кто проводит</div>
        <v-select
          v-model="form.expert"
          :items="expertOptions"
          item-text="fullName"
          item-value="id"
          :disabled="isEdit"
          outlined
          dense
          :hide-details="!isEdit"
          :hint="isEdit ? 'Эксперта интервала изменить нельзя — для другого эксперта создайте новый интервал' : ''"
          :persistent-hint="isEdit"
          class="mb-4"
        />

        <v-sheet v-if="selectedExpert" outlined rounded="lg" class="pa-3 mb-4 d-flex">
          <v-avatar size="40" color="#483CB9" class="mr-3">
            <span class="white--text text-caption">{{ initials(selectedExpert.fullName) }}</span>
          </v-avatar>
          <div>
            <div class="font-weight-medium text-body-2">{{ selectedExpert.fullName }}</div>
            <div class="text-caption grey--text mt-1">{{ selectedExpert.short_description }}</div>
          </div>
        </v-sheet>

        <div class="text-caption grey--text text--darken-1 mb-1">Начало</div>
        <v-row dense class="mb-2">
          <v-col cols="6">
            <v-text-field v-model="startDate" type="date" outlined dense hide-details />
          </v-col>
          <v-col cols="3">
            <v-select v-model="startHour" :items="hourOptions" outlined dense hide-details />
          </v-col>
          <v-col cols="3">
            <v-select v-model="startMinute" :items="minuteOptions" outlined dense hide-details />
          </v-col>
        </v-row>

        <div class="text-caption grey--text text--darken-1 mb-1">Окончание</div>
        <v-row dense class="mb-1">
          <v-col cols="6">
            <v-text-field v-model="endDate" type="date" outlined dense hide-details />
          </v-col>
          <v-col cols="3">
            <v-select v-model="endHour" :items="hourOptions" outlined dense hide-details />
          </v-col>
          <v-col cols="3">
            <v-select v-model="endMinute" :items="minuteOptions" outlined dense hide-details />
          </v-col>
        </v-row>
        <div class="text-caption grey--text mb-2">Минуты — только 00, 15, 30 или 45</div>
        <div v-if="rangeError" class="text-caption error--text mb-2">{{ rangeError }}</div>

        <template v-if="form.expert">
          <v-divider class="my-3" />
          <div class="text-caption grey--text text--darken-1 mb-1">Услуги в этом интервале</div>

          <div v-if="!expertServices.length" class="text-caption grey--text mb-1">
            У эксперта нет активных услуг — снимать нечего.
          </div>

          <template v-else-if="canEditServices">
            <div class="text-caption grey--text mb-2">
              Снимите галочку с услуги, которую эксперт не оказывает в это время.
            </div>
            <div
              v-for="service in expertServices"
              :key="service.id"
              class="d-flex align-center"
            >
              <v-checkbox
                :input-value="!isExcluded(service.id)"
                dense
                hide-details
                color="#483CB9"
                class="mt-0 pt-0"
                @change="toggleService(service.id, $event)"
              >
                <template #label>
                  <span class="text-body-2">
                    {{ serviceLabel(service) }}
                    <span v-if="service.is_active === false" class="text-caption error--text">
                      (услуга неактивна)
                    </span>
                  </span>
                </template>
              </v-checkbox>
            </div>
            <div v-if="allExcluded" class="text-caption error--text mt-1">
              Все услуги сняты — в этот интервал нельзя будет записаться.
            </div>
          </template>

          <div v-else class="text-caption grey--text">
            Менять перечень услуг можно только у будущих интервалов.
          </div>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-btn block color="#483CB9" :dark="canSave" :loading="loading" :disabled="!canSave" @click="save">
          {{ isEdit ? 'Сохранить изменения' : 'Добавить интервал' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { utcToLocalDateTimeInput, localDateTimeInputToUtcIso } from '@/common/calendarTime';

export default {
  name: 'AvailabilityFormDialog',
  props: {
    value: { type: Boolean, default: false },
    editingAvailability: { type: Object, default: null },
    defaultDate: { type: String, default: '' },
    experts: { type: Array, default: () => [] },
    services: { type: Array, default: () => [] },
    serviceTypes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      form: this.buildForm(),
      skipExpertWatch: false,
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.editingAvailability);
    },
    expertOptions() {
      // Только активные эксперты. Уже выбранного оставляем, даже если он неактивен,
      // чтобы при редактировании старого интервала поле не оказалось пустым.
      return this.experts
        .filter((expert) => expert.is_active === true || expert.id === this.form.expert)
        .map((expert) => ({
          ...expert,
          fullName: `${expert.first_name || ''} ${expert.last_name || ''}`.trim(),
        }));
    },
    selectedExpert() {
      return this.expertOptions.find((expert) => expert.id === this.form.expert) || null;
    },
    hourOptions() {
      return Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));
    },
    minuteOptions() {
      return ['00', '15', '30', '45'];
    },
    startDate: {
      get() { return this.datePart('startsAtLocal'); },
      set(value) { this.setDatePart('startsAtLocal', value); },
    },
    startHour: {
      get() { return this.hourPart('startsAtLocal'); },
      set(value) { this.setHourPart('startsAtLocal', value); },
    },
    startMinute: {
      get() { return this.minutePart('startsAtLocal'); },
      set(value) { this.setMinutePart('startsAtLocal', value); },
    },
    endDate: {
      get() { return this.datePart('endsAtLocal'); },
      set(value) { this.setDatePart('endsAtLocal', value); },
    },
    endHour: {
      get() { return this.hourPart('endsAtLocal'); },
      set(value) { this.setHourPart('endsAtLocal', value); },
    },
    endMinute: {
      get() { return this.minutePart('endsAtLocal'); },
      set(value) { this.setMinutePart('endsAtLocal', value); },
    },
    rangeError() {
      if (this.form.startsAtLocal && this.form.endsAtLocal && this.form.endsAtLocal <= this.form.startsAtLocal) {
        return 'Окончание должно быть позже начала';
      }
      return '';
    },
    canSave() {
      return Boolean(this.form.expert && this.form.startsAtLocal && this.form.endsAtLocal && !this.rangeError);
    },
    // Услуги эксперта: активные + те, что уже исключены в этом интервале (их бэк
    // сохраняет даже после деактивации — их нужно показать, чтобы можно было вернуть).
    expertServices() {
      if (!this.form.expert) return [];
      const excluded = new Set(this.form.unavailableServices);
      return this.services
        .filter((s) => s.expert === this.form.expert
          && (s.is_active !== false || excluded.has(s.id)))
        .sort((a, b) => this.serviceLabel(a).localeCompare(this.serviceLabel(b)));
    },
    // Менять перечень услуг можно только у открытого интервала, начинающегося в будущем.
    canEditServices() {
      if (!this.isEdit) return true;
      return new Date(this.editingAvailability.starts_at).getTime() > Date.now();
    },
    allExcluded() {
      return this.expertServices.length > 0
        && this.expertServices.every((s) => this.isExcluded(s.id));
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.resetForm();
    },
    'form.expert': function onExpertChange(next, prev) {
      // При смене эксперта пользователем услуги другие — сбрасываем исключения.
      // Первичное заполнение формы идёт через resetForm() с флагом skipExpertWatch.
      if (this.skipExpertWatch || prev === undefined) return;
      this.form.unavailableServices = [];
    },
  },
  methods: {
    resetForm() {
      this.skipExpertWatch = true;
      this.form = this.buildForm();
      this.$nextTick(() => { this.skipExpertWatch = false; });
    },
    buildForm() {
      if (this.editingAvailability) {
        return {
          expert: this.editingAvailability.expert,
          startsAtLocal: utcToLocalDateTimeInput(this.editingAvailability.starts_at),
          endsAtLocal: utcToLocalDateTimeInput(this.editingAvailability.ends_at),
          unavailableServices: [...(this.editingAvailability.unavailable_services || [])],
        };
      }
      const firstActiveExpert = this.experts.find((expert) => expert.is_active === true);
      return {
        expert: firstActiveExpert ? firstActiveExpert.id : null,
        startsAtLocal: this.defaultDate ? `${this.defaultDate}T10:00` : '',
        endsAtLocal: this.defaultDate ? `${this.defaultDate}T11:00` : '',
        unavailableServices: [],
      };
    },
    isExcluded(serviceId) {
      return this.form.unavailableServices.includes(serviceId);
    },
    toggleService(serviceId, isProvided) {
      const set = new Set(this.form.unavailableServices);
      if (isProvided) set.delete(serviceId);
      else set.add(serviceId);
      this.form.unavailableServices = [...set];
    },
    serviceLabel(service) {
      const type = this.serviceTypes.find((t) => t.id === service.service_type);
      const name = type ? type.name : `Услуга #${service.service_type}`;
      return service.max_duration_minutes
        ? `${name} · ${service.max_duration_minutes} мин`
        : name;
    },
    datePart(field) {
      return (this.form[field] || '').slice(0, 10);
    },
    hourPart(field) {
      return (this.form[field] || '').slice(11, 13) || '10';
    },
    minutePart(field) {
      return (this.form[field] || '').slice(14, 16) || '00';
    },
    setDatePart(field, value) {
      this.form[field] = `${value}T${this.hourPart(field)}:${this.minutePart(field)}`;
    },
    setHourPart(field, value) {
      this.form[field] = `${this.datePart(field)}T${value}:${this.minutePart(field)}`;
    },
    setMinutePart(field, value) {
      this.form[field] = `${this.datePart(field)}T${this.hourPart(field)}:${value}`;
    },
    initials(name) {
      return name.split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
    save() {
      if (!this.canSave) return;
      // Эксперт задаётся только при создании интервала.
      const payload = {
        id: this.editingAvailability ? this.editingAvailability.id : undefined,
        expert: this.isEdit ? undefined : this.form.expert,
        starts_at: localDateTimeInputToUtcIso(this.form.startsAtLocal),
        ends_at: localDateTimeInputToUtcIso(this.form.endsAtLocal),
      };
      // Отправляем список исключений только когда его можно менять (будущий открытый
      // интервал). Пустой массив = снять все исключения (бэк вернёт null).
      if (this.canEditServices) {
        payload.unavailable_services = [...this.form.unavailableServices];
      }
      this.$emit('save', payload);
    },
  },
};
</script>
